import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { createAuthenticatedClient } from '@/lib/graphql';
import { VIEWER_FEATURES_QUERY, type ViewerFeaturesData } from './queries';

interface FeatureFlagContextType {
  features: Set<string>;
  isLoading: boolean;
  hasFeature: (key: string) => boolean;
  refetch: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that fetches and caches the current user's enabled features.
 * Must be placed inside AuthProvider to access authentication state.
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const { isAuthenticated, accessToken } = useAuth();
  const [features, setFeatures] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const fetchFeatures = useCallback(async () => {
    if (!accessToken) {
      setFeatures(new Set());
      return;
    }

    setIsLoading(true);
    try {
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<ViewerFeaturesData>(VIEWER_FEATURES_QUERY);

      if (data.viewer) {
        setFeatures(new Set(data.viewer.features));
      } else {
        setFeatures(new Set());
      }
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
      setFeatures(new Set());
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      fetchFeatures();
    } else {
      setFeatures(new Set());
    }
  }, [isAuthenticated, accessToken, fetchFeatures]);

  const hasFeature = useCallback((key: string): boolean => {
    return features.has(key);
  }, [features]);

  return (
    <FeatureFlagContext.Provider
      value={{
        features,
        isLoading,
        hasFeature,
        refetch: fetchFeatures,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Hook to access the feature flag context.
 * Must be used within a FeatureFlagProvider.
 */
export function useFeatureFlagContext() {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }
  return context;
}
